import React, { useState, useEffect } from 'react';
import Sidebar from '../../components/dashboard/Sidebar';
import { BookOpen, Clock, User, CheckCircle, Circle } from 'lucide-react';
import axios from 'axios';

const DashboardCourses = () => {
  const [courses, setCourses] = useState([]);
  const [progress, setProgress] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [loading, setLoading] = useState(true);

  const STUDENT_ID = 'student-demo-1';
  const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

  useEffect(() => {
    fetchCoursesData();
  }, []);

  const fetchCoursesData = async () => {
    try {
      const coursesRes = await axios.get(`${API_URL}/api/dashboard/students/${STUDENT_ID}/courses`);
      setCourses(coursesRes.data);
      
      const progressRes = await axios.get(`${API_URL}/api/dashboard/students/${STUDENT_ID}/progress`);
      setProgress(progressRes.data);
      
      if (coursesRes.data.length > 0) {
        setSelectedCourse(coursesRes.data[0]);
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Error fetching courses:', error);
      setLoading(false);
    }
  };

  const getCourseProgress = (courseId) => {
    return progress.find(p => p.course_id === courseId) || {};
  };

  if (loading) {
    return (
      <div className="flex h-screen">
        <Sidebar />
        <div className="flex-1 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0D47A1]"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-gray-50">
      <Sidebar />
      
      <main className="flex-1 overflow-auto">
        {/* Header */}
        <div className="bg-white border-b border-gray-200 px-6 py-4">
          <h1 className="text-2xl font-bold text-gray-900">My Courses</h1>
          <p className="text-gray-600 mt-1">Track your progress and continue learning</p>
        </div>

        <div className="p-6 max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-3 gap-6">
            {/* Courses List */}
            <div className="lg:col-span-1 space-y-4">
              {courses.map((course) => {
                const courseProgress = getCourseProgress(course.id);
                const isSelected = selectedCourse?.id === course.id;
                
                return (
                  <div
                    key={course.id}
                    onClick={() => setSelectedCourse(course)}
                    className={`bg-white rounded-2xl p-4 border cursor-pointer transition-all ${
                      isSelected 
                        ? 'border-[#00BCD4] shadow-lg' 
                        : 'border-gray-200 hover:border-[#0D47A1]'
                    }`}
                  >
                    <img 
                      src={course.thumbnail} 
                      alt={course.title}
                      className="w-full h-32 object-cover rounded-xl mb-3"
                    />
                    <h3 className="font-bold text-gray-900 mb-2">{course.title}</h3>
                    
                    <div className="flex items-center gap-2 text-sm text-gray-600 mb-3">
                      <User className="w-4 h-4" />
                      <span>{course.instructor}</span>
                    </div>
                    
                    <div className="mb-2">
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-gray-600">Progress</span>
                        <span className="font-bold text-[#0D47A1]">{courseProgress.progress_percentage || 0}%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-gradient-to-r from-[#0D47A1] to-[#00BCD4] h-2 rounded-full"
                          style={{ width: `${courseProgress.progress_percentage || 0}%` }}
                        ></div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Course Details */}
            {selectedCourse && (
              <div className="lg:col-span-2 bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
                <img 
                  src={selectedCourse.thumbnail} 
                  alt={selectedCourse.title}
                  className="w-full h-64 object-cover rounded-xl mb-6"
                />
                
                <h2 className="text-3xl font-bold text-gray-900 mb-4">{selectedCourse.title}</h2>
                <p className="text-gray-600 mb-6">{selectedCourse.description}</p>
                
                <div className="flex items-center gap-6 mb-6 pb-6 border-b border-gray-200">
                  <div className="flex items-center gap-2">
                    <User className="w-5 h-5 text-gray-600" />
                    <span className="text-gray-700">{selectedCourse.instructor}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock className="w-5 h-5 text-gray-600" />
                    <span className="text-gray-700">{selectedCourse.duration}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <BookOpen className="w-5 h-5 text-gray-600" />
                    <span className="text-gray-700">{selectedCourse.modules.length} Modules</span>
                  </div>
                </div>

                {/* Modules List */}
                <h3 className="text-xl font-bold text-gray-900 mb-4">Course Modules</h3>
                <div className="space-y-3">
                  {selectedCourse.modules.map((module, index) => {
                    const courseProgress = getCourseProgress(selectedCourse.id);
                    const isCompleted = courseProgress.completed_modules?.includes(module.id);
                    
                    return (
                      <div
                        key={module.id}
                        className={`flex items-start gap-4 p-4 rounded-xl border transition-all ${
                          isCompleted 
                            ? 'bg-green-50 border-green-200' 
                            : 'bg-gray-50 border-gray-200 hover:border-[#00BCD4]'
                        }`}
                      >
                        <div className="flex-shrink-0 mt-1">
                          {isCompleted ? (
                            <CheckCircle className="w-6 h-6 text-green-600" />
                          ) : (
                            <Circle className="w-6 h-6 text-gray-400" />
                          )}
                        </div>
                        
                        <div className="flex-1">
                          <h4 className="font-bold text-gray-900 mb-1">
                            Module {index + 1}: {module.title}
                          </h4>
                          <p className="text-sm text-gray-600 mb-2">{module.description}</p>
                          <div className="flex items-center gap-2 text-sm text-gray-500">
                            <Clock className="w-4 h-4" />
                            <span>{module.duration}</span>
                          </div>
                        </div>
                        
                        {!isCompleted && (
                          <button className="px-4 py-2 bg-[#0D47A1] text-white rounded-lg hover:bg-[#00BCD4] transition-colors">
                            Start
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>

                <div className="mt-6 flex gap-4">
                  <button className="flex-1 bg-gradient-to-r from-[#0D47A1] to-[#00BCD4] text-white font-bold py-3 rounded-xl hover:shadow-lg transition-all">
                    Resume Learning
                  </button>
                  <button className="px-6 py-3 border-2 border-[#0D47A1] text-[#0D47A1] font-bold rounded-xl hover:bg-blue-50 transition-all">
                    View Resources
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default DashboardCourses;